import { Suspense, type ReactNode } from 'react'
import { useAuth as useClerkAuth } from '@clerk/clerk-react'
import { Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom'
import Shell from './components/layout/Shell'
import DocumentTitle from './components/layout/DocumentTitle'
import ErrorBoundary from './components/ErrorBoundary'
import RequirePermission from './components/RequirePermission'
import RequireAnyPermission from './components/RequireAnyPermission'
import RequirePlatformAdmin from './components/RequirePlatformAdmin'
import RequireOwnerOrManager from './components/RequireOwnerOrManager'
import RequireModule from './components/RequireModule'
import OnboardingSetupRedirect from './components/onboarding/OnboardingSetupRedirect'
import BackofficeAccessDenied from './components/BackofficeAccessDenied'
import AccountLoadError from './components/AccountLoadError'
import LoadingScreen from './components/LoadingScreen'
import WelcomePage from './pages/WelcomePage'
import EntryPage from './pages/EntryPage'
import NotFound from './pages/NotFoundPage'
import { useAuth } from './contexts/AuthContext'
import type { CompanyApprovalStatus } from './contexts/AuthContext'
import { e2eToken } from './lib/api'
import { lazyPage } from './lib/staleChunk'
import { entryPathFor, hasEntryContext, personaHomePath, welcomePathFor } from './lib/entryContext'
import { REWARDS_MODULES, REWARDS_PERMISSIONS } from './components/layout/navConfig'

// Pages load on demand, so a visit downloads the shell plus the one page it
// opens instead of all of them. Each import points at the page module itself:
// going through a barrel (./pages) would pull every page back into the entry
// chunk and undo the split.
const Categories = lazyPage(() => import('./pages/CategoriesPage'))
const Company = lazyPage(() => import('./pages/CompanyPage'))
const PublicHolidays = lazyPage(() => import('./pages/PublicHolidaysPage'))
const CompanyPackage = lazyPage(() => import('./pages/CompanyPackagePage'))
const CompanyInvoices = lazyPage(() => import('./pages/CompanyInvoicesPage'))
const ShopPage = lazyPage(() => import('./pages/ShopPage'))
const Calendar = lazyPage(() => import('./pages/CalendarPage'))
const Events = lazyPage(() => import('./pages/EventsPage'))
const EventAttendance = lazyPage(() => import('./pages/EventAttendancePage'))
const Inventory = lazyPage(() => import('./pages/InventoryPage'))
const InventoryItem = lazyPage(() => import('./pages/InventoryItemPage'))
const Addons = lazyPage(() => import('./pages/AddonsPage'))
const Offerings = lazyPage(() => import('./pages/OfferingsPage'))
const Orders = lazyPage(() => import('./pages/OrdersPage'))
const ManageOrders = lazyPage(() => import('./pages/ManageOrdersPage'))
const Clients = lazyPage(() => import('./pages/ClientsPage'))
const ClientTags = lazyPage(() => import('./pages/ClientTagsPage'))
const ClientInvites = lazyPage(() => import('./pages/ClientInvitesPage'))
const ClientDetail = lazyPage(() => import('./pages/ClientDetailPage'))
const RewardsOverview = lazyPage(() => import('./pages/rewards/RewardsOverviewPage'))
const RewardsPromoCodes = lazyPage(() => import('./pages/rewards/RewardsPromoCodesPage'))
const RewardsLoyalty = lazyPage(() => import('./pages/rewards/RewardsLoyaltyPage'))
const RewardsAwards = lazyPage(() => import('./pages/rewards/RewardsAwardsPage'))
const RewardsReport = lazyPage(() => import('./pages/rewards/RewardsReportPage'))
const RewardsCampaigns = lazyPage(() => import('./pages/rewards/RewardsCampaignsPage'))
const Stores = lazyPage(() => import('./pages/StoresPage'))
const Devices = lazyPage(() => import('./pages/DevicesPage'))
const Stock = lazyPage(() => import('./pages/StockPage'))
const Shifts = lazyPage(() => import('./pages/ShiftsPage'))
const GenerateShifts = lazyPage(() => import('./pages/GenerateShiftsPage'))
const Workers = lazyPage(() => import('./pages/WorkersPage'))
const Dashboard = lazyPage(() => import('./pages/DashboardPage'))
const Reporting = lazyPage(() => import('./pages/ReportingPage'))
const Subscriptions = lazyPage(() => import('./pages/SubscriptionsPage'))
const PlanConfigs = lazyPage(() => import('./pages/PlanConfigsPage'))
const CourierDeliveries = lazyPage(() => import('./pages/CourierDeliveriesPage'))
const CourierEarnings = lazyPage(() => import('./pages/CourierEarningsPage'))
const MerchantWallet = lazyPage(() => import('./pages/MerchantWalletPage'))
const PartnerOrders = lazyPage(() => import('./pages/PartnerOrdersPage'))
const PartnerCoverage = lazyPage(() => import('./pages/PartnerCoveragePage'))
const Support = lazyPage(() => import('./pages/SupportPage'))
const SupportThread = lazyPage(() => import('./pages/SupportThreadPage'))
const Help = lazyPage(() => import('./pages/HelpPage'))
const HelpArticle = lazyPage(() => import('./pages/HelpArticlePage'))

const PlatformDashboard = lazyPage(() => import('./pages/platform/PlatformDashboard'))
const PlatformCompaniesPage = lazyPage(() => import('./pages/platform/PlatformCompaniesPage'))
const PlatformPackagesPage = lazyPage(() => import('./pages/platform/PlatformPackagesPage'))
const PlatformCatalogPage = lazyPage(() => import('./pages/platform/PlatformCatalogPage'))
const PlatformUsersPage = lazyPage(() => import('./pages/platform/PlatformUsersPage'))
const PlatformCouriersPage = lazyPage(() => import('./pages/platform/PlatformCouriersPage'))
const PlatformDispatchPage = lazyPage(() => import('./pages/platform/PlatformDispatchPage'))
const PlatformSupportPage = lazyPage(() => import('./pages/platform/PlatformSupportPage'))
const PlatformSupportThreadPage = lazyPage(() => import('./pages/platform/PlatformSupportThreadPage'))
const PlatformDevicesPage = lazyPage(() => import('./pages/platform/PlatformDevicesPage'))
const PlatformWalletPage = lazyPage(() => import('./pages/platform/PlatformWalletPage'))
const PlatformPaymentsPage = lazyPage(() => import('./pages/platform/PlatformPaymentsPage'))
const PlatformInvoicesPage = lazyPage(() => import('./pages/platform/PlatformInvoicesPage'))
const PlatformPromoCodesPage = lazyPage(() => import('./pages/platform/PlatformPromoCodesPage'))
const PlatformLoyaltyPage = lazyPage(() => import('./pages/platform/PlatformLoyaltyPage'))
const PlatformCampaignsPage = lazyPage(() => import('./pages/platform/PlatformCampaignsPage'))
const PlatformCampaignPage = lazyPage(() => import('./pages/platform/PlatformCampaignPage'))
const PlatformRightsPage = lazyPage(() => import('./pages/platform/PlatformRightsPage'))
const PlatformSystemPage = lazyPage(() => import('./pages/platform/PlatformSystemPage'))
const PlatformAiPage = lazyPage(() => import('./pages/platform/PlatformAiPage'))

const OnboardingApplyPage = lazyPage(() => import('./pages/onboarding/OnboardingApplyPage'))
const OnboardingPendingPage = lazyPage(() => import('./pages/onboarding/OnboardingPendingPage'))
const OnboardingRejectedPage = lazyPage(() => import('./pages/onboarding/OnboardingRejectedPage'))
const OnboardingWizardPage = lazyPage(() => import('./pages/onboarding/OnboardingWizardPage'))

type OnboardingRouteKey = 'apply' | 'pending' | 'rejected'

/** Persona-aware home: Admin → platform, Courier → deliveries, else merchant dashboard. */
function HomeRedirect() {
  const { activePersona } = useAuth()
  return <Navigate to={personaHomePath(activePersona)} replace />
}

/** The old item wizard's address (/inventory/:id/setup): the item's own page edits it now (#193). */
function InventorySetupRedirect() {
  const { id = '' } = useParams<{ id: string }>()
  return <Navigate to={`/inventory/${encodeURIComponent(id)}`} replace />
}

/**
 * Platform › Promo codes moved under Platform › Rewards: an old link or bookmark
 * lands on the new address with its filters (query string and hash) kept.
 */
function PlatformRewardsRedirect() {
  const { search, hash } = useLocation()
  return <Navigate to={{ pathname: '/platform/rewards/promo-codes', search, hash }} replace />
}

/**
 * Rewards: catalog:write changes, orders:read looks, and the company's package
 * needs promo codes or a loyalty card (the menu hides it otherwise; this is for
 * a link or a bookmark).
 */
function RewardsGate({ children }: { children: ReactNode }) {
  return (
    <RequireAnyPermission permissions={REWARDS_PERMISSIONS}>
      <RequireModule anyModule={REWARDS_MODULES}>{children}</RequireModule>
    </RequireAnyPermission>
  )
}

/** Merchant Flexible Dashboard is not an Admin, Courier or Partner surface: each
 *  of those personas goes to its own home instead. Courier and Partner only when
 *  they still hold the permission their own page asks for — that page sends
 *  anyone without it back here, so redirecting regardless would bounce them
 *  between the two for ever. */
function MerchantDashboardGate() {
  const { activePersona, hasPermission } = useAuth()
  if (activePersona === 'Admin') return <Navigate to="/platform" replace />
  if (activePersona === 'Courier' && hasPermission('courier:orders:read')) {
    return <Navigate to="/courier/deliveries" replace />
  }
  if (activePersona === 'Partner' && hasPermission('partner:orders:read')) {
    return <Navigate to="/partner/orders" replace />
  }
  return <Dashboard />
}

/** Shown while a route's chunk downloads. It sits inside the shell, so the
 *  header and the side menu stay on screen and only the content area waits. */
function RouteFallback() {
  return (
    <div
      role="status"
      className="flex min-h-[50vh] items-center justify-center text-sm text-foreground-muted motion-safe:animate-fade-in"
    >
      Loading…
    </div>
  )
}

function OnboardingOnlyRoutes({ allowed }: { allowed: OnboardingRouteKey[] }) {
  const location = useLocation()
  const defaultPath = `/onboarding/${allowed[0]}`
  const currentAllowed = allowed.some((key) => location.pathname === `/onboarding/${key}`)

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {allowed.includes('apply') && (
            <Route path="/onboarding/apply" element={<OnboardingApplyPage />} />
          )}
          {allowed.includes('pending') && (
            <Route path="/onboarding/pending" element={<OnboardingPendingPage />} />
          )}
          {allowed.includes('rejected') && (
            <Route path="/onboarding/rejected" element={<OnboardingRejectedPage />} />
          )}
          <Route
            path="*"
            element={<Navigate to={currentAllowed ? location.pathname : defaultPath} replace />}
          />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}

function MainShellRoutes() {
  return (
    <Shell>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/dashboard" element={<MerchantDashboardGate />} />
          <Route path="/onboarding" element={<OnboardingWizardPage />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/company" element={<Company />} />
          <Route
            path="/company/holidays"
            element={
              <RequirePermission permission="company:read">
                <PublicHolidays />
              </RequirePermission>
            }
          />
          <Route
            path="/company/package"
            element={
              <RequirePermission permission="company:read">
                <CompanyPackage />
              </RequirePermission>
            }
          />
          <Route
            path="/company/shop"
            element={
              <RequirePermission permission="company:read">
                <RequireModule module="shop_page">
                  <ShopPage />
                </RequireModule>
              </RequirePermission>
            }
          />
          <Route
            path="/company/invoices"
            element={
              <RequirePermission permission="company:read">
                <RequireModule module="invoices">
                  <CompanyInvoices />
                </RequireModule>
              </RequirePermission>
            }
          />
          <Route path="/calendar" element={<RequireModule module="bookings"><Calendar /></RequireModule>} />
          <Route path="/events" element={<RequireModule module="events"><Events /></RequireModule>} />
          <Route
            path="/events/:occurrenceId/attendance"
            element={<RequireModule module="events"><EventAttendance /></RequireModule>}
          />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/inventory/new" element={<InventoryItem />} />
          <Route path="/inventory/:id" element={<InventoryItem />} />
          <Route path="/inventory/:id/setup" element={<InventorySetupRedirect />} />
          <Route path="/addons" element={<Addons />} />
          <Route path="/offerings" element={<Offerings />} />
          <Route path="/plans" element={<RequireModule module="client_plans"><Subscriptions /></RequireModule>} />
          <Route path="/plans/configs" element={<RequireModule module="client_plans"><PlanConfigs /></RequireModule>} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/manage" element={<ManageOrders />} />
          <Route
            path="/clients"
            element={
              <RequirePermission permission="orders:read">
                <Clients />
              </RequirePermission>
            }
          />
          <Route
            path="/clients/tags"
            element={
              <RequirePermission permission="orders:read">
                <ClientTags />
              </RequirePermission>
            }
          />
          <Route
            path="/clients/invites"
            element={
              <RequirePermission permission="orders:read">
                <ClientInvites />
              </RequirePermission>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <RequirePermission permission="orders:read">
                <ClientDetail />
              </RequirePermission>
            }
          />
          <Route path="/rewards" element={<RewardsGate><RewardsOverview /></RewardsGate>} />
          <Route
            path="/rewards/promo-codes"
            element={
              <RewardsGate>
                <RequireModule module="promo_codes">
                  <RewardsPromoCodes />
                </RequireModule>
              </RewardsGate>
            }
          />
          <Route
            path="/rewards/loyalty"
            element={
              <RewardsGate>
                <RequireModule module="loyalty">
                  <RewardsLoyalty />
                </RequireModule>
              </RewardsGate>
            }
          />
          <Route
            path="/rewards/campaigns"
            element={
              <RewardsGate>
                <RequireModule module="promo_codes">
                  <RewardsCampaigns />
                </RequireModule>
              </RewardsGate>
            }
          />
          <Route path="/rewards/awards" element={<RewardsGate><RewardsAwards /></RewardsGate>} />
          {/* The same page as Clients › Invite links, as a second menu entry. */}
          <Route
            path="/rewards/invites"
            element={
              <RequirePermission permission="orders:read">
                <RequireModule anyModule={REWARDS_MODULES}>
                  <ClientInvites />
                </RequireModule>
              </RequirePermission>
            }
          />
          <Route path="/rewards/report" element={<RewardsGate><RewardsReport /></RewardsGate>} />
          <Route path="/stores" element={<Stores />} />
          <Route
            path="/devices"
            element={
              <RequireOwnerOrManager>
                <RequireModule module="devices">
                  <Devices />
                </RequireModule>
              </RequireOwnerOrManager>
            }
          />
          <Route path="/stock" element={<Stock />} />
          <Route path="/shifts" element={<RequireModule module="shifts"><Shifts /></RequireModule>} />
          <Route
            path="/shifts/generate"
            element={
              <RequirePermission permission="schedule:generate">
                <RequireModule module="shifts">
                  <GenerateShifts />
                </RequireModule>
              </RequirePermission>
            }
          />
          <Route
            path="/workers"
            element={
              <RequireAnyPermission permissions={['workers:write', 'managers:write']}>
                <Workers />
              </RequireAnyPermission>
            }
          />
          <Route path="/reporting" element={<RequireModule module="reports"><Reporting /></RequireModule>} />
          <Route
            path="/courier/deliveries"
            element={
              <RequirePermission permission="courier:orders:read" redirectTo="/dashboard">
                <CourierDeliveries />
              </RequirePermission>
            }
          />
          <Route
            path="/courier/earnings"
            element={
              <RequirePermission permission="courier:orders:read" redirectTo="/dashboard">
                <CourierEarnings />
              </RequirePermission>
            }
          />
          <Route
            path="/partner/orders"
            element={
              <RequirePermission permission="partner:orders:read" redirectTo="/dashboard">
                <PartnerOrders />
              </RequirePermission>
            }
          />
          <Route
            path="/partner/coverage"
            element={
              <RequireAnyPermission
                permissions={['partner:zones:read', 'partner:rates:read']}
                redirectTo="/dashboard"
              >
                <PartnerCoverage />
              </RequireAnyPermission>
            }
          />
          <Route
            path="/wallet"
            element={
              <RequirePermission permission="wallet:read" redirectTo="/dashboard">
                <MerchantWallet />
              </RequirePermission>
            }
          />
          {/* Help & support: any signed-in requester; the side is the server's call. */}
          <Route path="/support" element={<Support />} />
          <Route path="/support/:id" element={<SupportThread />} />
          {/* The help centre: any signed-in user; which articles they see is the server's call. */}
          <Route path="/help" element={<Help />} />
          <Route path="/help/:slug" element={<HelpArticle />} />
          <Route
            path="/platform"
            element={
              <RequirePlatformAdmin>
                <PlatformDashboard />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/companies"
            element={
              <RequirePlatformAdmin>
                <PlatformCompaniesPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/packages"
            element={
              <RequirePlatformAdmin>
                <PlatformPackagesPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/catalog"
            element={
              <RequirePlatformAdmin>
                <PlatformCatalogPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/users"
            element={
              <RequirePlatformAdmin>
                <PlatformUsersPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/couriers"
            element={
              <RequirePlatformAdmin>
                <PlatformCouriersPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/dispatch"
            element={
              <RequirePlatformAdmin>
                <PlatformDispatchPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/support"
            element={
              <RequirePlatformAdmin>
                <PlatformSupportPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/support/:id"
            element={
              <RequirePlatformAdmin>
                <PlatformSupportThreadPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/devices"
            element={
              <RequirePlatformAdmin>
                <PlatformDevicesPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/payments"
            element={
              <RequirePlatformAdmin>
                <PlatformPaymentsPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/invoices"
            element={
              <RequirePlatformAdmin>
                <PlatformInvoicesPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/rewards/promo-codes"
            element={
              <RequirePlatformAdmin>
                <PlatformPromoCodesPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/rewards/loyalty"
            element={
              <RequirePlatformAdmin>
                <PlatformLoyaltyPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/rewards/campaigns"
            element={
              <RequirePlatformAdmin>
                <PlatformCampaignsPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/rewards/campaigns/:id"
            element={
              <RequirePlatformAdmin>
                <PlatformCampaignPage />
              </RequirePlatformAdmin>
            }
          />
          {/* Platform › Rewards opens its first entry; the old Promo codes address redirects. */}
          <Route path="/platform/rewards" element={<PlatformRewardsRedirect />} />
          <Route path="/platform/promo-codes" element={<PlatformRewardsRedirect />} />
          <Route
            path="/platform/wallet"
            element={
              <RequirePlatformAdmin>
                <PlatformWalletPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/rights"
            element={
              <RequirePlatformAdmin>
                <PlatformRightsPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/system"
            element={
              <RequirePlatformAdmin>
                <PlatformSystemPage />
              </RequirePlatformAdmin>
            }
          />
          <Route
            path="/platform/ai"
            element={
              <RequirePlatformAdmin>
                <PlatformAiPage />
              </RequirePlatformAdmin>
            }
          />
          {/* Only applicants use apply, pending and rejected: anyone else here goes home, not to Not found. */}
          <Route path="/onboarding/*" element={<HomeRedirect />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Shell>
  )
}

function onboardingRoutesForStatus(status: CompanyApprovalStatus): OnboardingRouteKey[] {
  switch (status) {
    case 'pending':
      return ['pending', 'apply']
    case 'rejected':
      return ['rejected', 'apply']
    default:
      return ['apply']
  }
}

function AuthenticatedApp() {
  const {
    loading: roleLoading,
    accountLoadError,
    retryAccountLoad,
    workerOnly,
    isPlatformAdmin,
    isCourier,
    isCourierPartner,
    companyId,
    companyApprovalStatus,
  } = useAuth()

  if (roleLoading) {
    return <LoadingScreen />
  }

  // A failed account load is not "no company": say so, instead of showing the apply form.
  if (accountLoadError) {
    return <AccountLoadError detail={accountLoadError} onRetry={retryAccountLoad} />
  }

  // No company perms, not admin, not courier → access denied.
  if (workerOnly) {
    return <BackofficeAccessDenied />
  }

  // Platform admins and courier-only users skip company onboarding gates.
  if (isPlatformAdmin || (isCourier && !companyId) || (isCourierPartner && !companyId)) {
    return (
      <ErrorBoundary>
        <MainShellRoutes />
      </ErrorBoundary>
    )
  }

  if (companyApprovalStatus !== 'approved') {
    return (
      <OnboardingOnlyRoutes allowed={onboardingRoutesForStatus(companyApprovalStatus)} />
    )
  }

  return (
    <ErrorBoundary>
      <OnboardingSetupRedirect />
      <MainShellRoutes />
    </ErrorBoundary>
  )
}

function RootRoute({ isAuthenticated }: { isAuthenticated: boolean }) {
  const [searchParams] = useSearchParams()

  // Clerk comes back here after sign-in, so `next` — the page the link asked
  // for — is handed on to the picker instead of being dropped.
  if (isAuthenticated) {
    return <Navigate to={entryPathFor(searchParams.get('next') ?? '')} replace />
  }
  return <WelcomePage />
}

function EntryRoute({ isAuthenticated }: { isAuthenticated: boolean }) {
  const [searchParams] = useSearchParams()

  // A link straight to /entry?next=… keeps its target through the Welcome page.
  if (!isAuthenticated) {
    return <Navigate to={welcomePathFor(searchParams.get('next') ?? '')} replace />
  }
  return <EntryPage />
}

function ProtectedRoute({ isAuthenticated }: { isAuthenticated: boolean }) {
  const { loading: roleLoading } = useAuth()
  const location = useLocation()
  const isOnboardingRoute = location.pathname.startsWith('/onboarding/')
  const isPlatformRoute = location.pathname.startsWith('/platform')

  // The Welcome page carries the page that was asked for, so it survives Clerk.
  if (!isAuthenticated) {
    return <Navigate to={welcomePathFor(`${location.pathname}${location.search}${location.hash}`)} replace />
  }

  // Merchant apply/pending/rejected must work before the entry picker sets context.
  // Platform admin deep links (e.g. /platform/ai) skip the entry picker gate.
  // The picker gets the page that was asked for, so it can go there after Continue.
  if (!hasEntryContext() && !isOnboardingRoute && !isPlatformRoute) {
    return <Navigate to={entryPathFor(`${location.pathname}${location.search}${location.hash}`)} replace />
  }

  if (roleLoading) {
    return <LoadingScreen />
  }

  return <AuthenticatedApp />
}



export default function App() {
  const { isLoaded, isSignedIn } = useClerkAuth()

  const bypassToken = e2eToken()
  const isLoading = bypassToken ? false : !isLoaded
  const isAuthenticated = bypassToken ? true : !!isSignedIn

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <>
      <DocumentTitle />
      <Routes>
        <Route path="/" element={<RootRoute isAuthenticated={isAuthenticated} />} />
        <Route path="/entry" element={<EntryRoute isAuthenticated={isAuthenticated} />} />
        <Route path="/*" element={<ProtectedRoute isAuthenticated={isAuthenticated} />} />
      </Routes>
    </>
  )
}
